/**
 * StaffIDCardModal
 * ================
 * The on-screen, scan-at-the-gate digital staff badge.
 *
 * Visual hierarchy on the badge:
 *   • Brand-coloured header strip with org logo + name
 *   • Big avatar + Full name + Role
 *   • Bold staff ID code (KWA-AU-5545) — the "show this to the school"
 *     field — plus a "✓ Authorised staff" green pill
 *   • QR code linking to the public /verify/staff-id/<token> page so the
 *     receiving school can scan + confirm the card live (works even if
 *     the staff member is offline at that exact moment, since the QR
 *     resolves on the receiving device)
 *   • Issued / Expires footer
 *
 * Actions in the modal footer:
 *   • Download PNG — uses an offscreen canvas to rasterise the badge so
 *     staff can save it to their phone gallery for printing on a plastic
 *     card or quick offline display
 *   • Copy share link — same URL the QR encodes; convenient for SMS
 *   • Rotate token — invalidates the existing link (e.g. after a screenshot
 *     leak). Confirms before doing it.
 */
import React, { useEffect, useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const BRAND_GRADIENT = 'linear-gradient(135deg, #00abc9 0%, #2abb9b 100%)';

export const StaffIDCardModal = ({ open, onClose, userId = null, targetLabel = null }) => {
  // `userId` — when set, the modal fetches the target user's ID card
  //             via the admin endpoints (for use inside the Users tab
  //             staff profile drawer). Photo upload/removal is hidden
  //             in this mode since those endpoints are self-only.
  // `targetLabel` — optional display name of the target user (used in
  //             confirmation dialogs so the admin sees whose token
  //             they're about to rotate).
  const isAdminView = Boolean(userId);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const badgeRef = useRef(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    const loader = isAdminView
      ? api.getStaffIdForUser(userId)
      : api.getMyStaffId();
    loader
      .then(c => { if (!cancelled) setCard(c); })
      .catch(err => { if (!cancelled) toast.error(err.message || 'Failed to load ID card'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, isAdminView, userId]);

  async function handleRotate() {
    const who = targetLabel ? ` for ${targetLabel}` : '';
    if (!window.confirm(`Rotate this ID card link${who}? Any previously shared link will stop working immediately.`)) return;
    setBusy(true);
    try {
      const fresh = isAdminView
        ? await api.rotateStaffIdForUser(userId)
        : await api.rotateMyStaffId();
      setCard(fresh);
      toast.success('A fresh ID link has been issued.');
    } catch (e) {
      toast.error(e.message || 'Could not rotate the ID card');
    } finally {
      setBusy(false);
    }
  }

  // ---- Avatar upload (replaces the initial-letter fallback with a real photo) ----
  async function handlePhotoSelected(e) {
    const file = e.target.files && e.target.files[0];
    // Clear the input first so picking the SAME file again still fires onChange.
    try { e.target.value = ''; } catch { /* ignore */ }
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Photo is too large (max 8 MB).');
      return;
    }
    setPhotoBusy(true);
    try {
      const out = await api.uploadMyAvatar(file);
      // The backend now distinguishes between an auto-approved upload
      // (manager roles) and one that needs verification (everyone else).
      // We reflect that state in the modal so the staff member isn't
      // surprised when their badge still shows initials after upload.
      const buster = `?v=${Date.now()}`;
      if (out?.avatar_status === 'pending') {
        setCard(c => c ? {
          ...c,
          // DO NOT touch avatar_url — badge keeps showing initials until
          // a manager approves the pending photo.
          avatar_status: 'pending',
          avatar_pending_url: `${out.avatar_pending_url}${buster}`,
          avatar_pending_uploaded_at: new Date().toISOString(),
          avatar_rejection_reason: null,
        } : c);
        toast.success(
          out?.message ||
          'Photo uploaded — a manager will review it before it appears on your ID card.'
        );
      } else {
        // Auto-approved (the caller is a manager).
        setCard(c => c ? {
          ...c,
          avatar_url: `${out.avatar_url}${buster}`,
          avatar_status: 'approved',
          avatar_pending_url: null,
          avatar_pending_uploaded_at: null,
          avatar_rejection_reason: null,
        } : c);
        toast.success('Photo updated — your ID card now shows your face.');
      }
    } catch (err) {
      toast.error(err.message || 'Could not upload the photo');
    } finally {
      setPhotoBusy(false);
    }
  }

  async function handlePhotoRemove() {
    if (!window.confirm('Remove your profile photo? Your ID card will fall back to your initials.')) return;
    setPhotoBusy(true);
    try {
      await api.deleteMyAvatar();
      setCard(c => c ? {
        ...c,
        avatar_url: null,
        avatar_status: null,
        avatar_pending_url: null,
        avatar_pending_uploaded_at: null,
        avatar_rejection_reason: null,
      } : c);
      toast.success('Photo removed.');
    } catch (err) {
      toast.error(err.message || 'Could not remove the photo');
    } finally {
      setPhotoBusy(false);
    }
  }

  function handleCopy() {
    if (!card?.verify_url) return;
    navigator.clipboard.writeText(card.verify_url)
      .then(() => toast.success('Verification link copied'))
      .catch(() => toast.error('Could not copy — long-press the link to copy manually'));
  }

  async function handleDownload() {
    // Compose the badge on an offscreen canvas at 2x resolution so it
    // prints crisply at typical ID-card sizes (≈ 86×54mm). The QR is
    // already a canvas element thanks to qrcode.react — we just draw
    // text + a gradient header on top of a white card.
    if (!card) return;
    const W = 720, H = 1160;
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');

    // Card background
    ctx.fillStyle = '#ffffff';
    roundRect(ctx, 0, 0, W, H, 36); ctx.fill();

    // Brand header strip
    const grad = ctx.createLinearGradient(0, 0, W, 220);
    grad.addColorStop(0, '#00abc9');
    grad.addColorStop(1, '#2abb9b');
    ctx.fillStyle = grad;
    roundRect(ctx, 0, 0, W, 220, [36, 36, 0, 0]); ctx.fill();

    // Org name
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 32px Figtree, system-ui, sans-serif';
    ctx.fillText(card.organization?.name || 'Kids WiseApp', 40, 90);
    ctx.font = '600 18px Figtree, system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fillText('STAFF IDENTIFICATION', 40, 130);

    // Avatar — centered hero on the card, ~260px diameter for legibility
    // even when printed small. Load the real photo if we have one,
    // otherwise the teal-circle initial fallback.
    const ar = 130;
    const ax = (W / 2) - ar;   // centered horizontally
    const ay = 250;
    if (card.avatar_url) {
      try {
        const photoImg = await new Promise((resolve, reject) => {
          const im = new Image();
          // Allow cross-origin reads so we can paint to the canvas.
          im.crossOrigin = 'anonymous';
          im.onload = () => resolve(im);
          im.onerror = reject;
          // Bust any cached HEAD-without-CORS response.
          im.src = `${card.avatar_url}${card.avatar_url.includes('?') ? '&' : '?'}cors=1`;
        });
        // Circular mask.
        ctx.save();
        ctx.beginPath();
        ctx.arc(ax + ar, ay + ar, ar, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(photoImg, ax, ay, ar * 2, ar * 2);
        ctx.restore();
        // Cyan ring around the photo.
        ctx.lineWidth = 6;
        ctx.strokeStyle = '#00abc9';
        ctx.beginPath();
        ctx.arc(ax + ar, ay + ar, ar, 0, Math.PI * 2);
        ctx.stroke();
      } catch {
        // CORS or load failure — fall back to initials so download still works.
        drawInitialAvatar(ctx, ax, ay, ar, card.full_name || '?');
      }
    } else {
      drawInitialAvatar(ctx, ax, ay, ar, card.full_name || '?');
    }

    // Name + role (centered under avatar)
    ctx.fillStyle = '#0a1f24';
    ctx.font = 'bold 44px Figtree, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(card.full_name || 'Staff', W / 2, ay + ar * 2 + 52);
    ctx.fillStyle = '#4b6b6f';
    ctx.font = '600 22px Figtree, system-ui, sans-serif';
    const roleLine = card.assigned_room_name
      ? `${card.role_label || 'Staff'} · ${card.assigned_room_name}`
      : (card.role_label || 'Staff');
    ctx.fillText(roleLine, W / 2, ay + ar * 2 + 84);
    ctx.textAlign = 'start';

    // ID code box — sits below the role line.
    ctx.fillStyle = '#f4fbfc';
    roundRect(ctx, 40, 620, W - 80, 96, 18); ctx.fill();
    ctx.fillStyle = '#4b6b6f';
    ctx.font = '700 14px Figtree, system-ui, sans-serif';
    ctx.fillText('STAFF ID', 60, 650);
    ctx.fillStyle = '#0a1f24';
    ctx.font = 'bold 38px ui-monospace, Menlo, monospace';
    ctx.fillText(card.staff_id_code || '—', 60, 695);

    // Authorised pill
    ctx.fillStyle = '#dbf5ef';
    roundRect(ctx, 40, 740, 380, 54, 27); ctx.fill();
    ctx.fillStyle = '#0b6b58';
    ctx.font = 'bold 19px Figtree, system-ui, sans-serif';
    ctx.fillText('✓  AUTHORISED STAFF', 64, 775);

    // Pull the QR from the live <canvas> rendered by qrcode.react so we
    // don't have to re-encode here. It's tucked away in the badge DOM.
    const qrCanvas = badgeRef.current?.querySelector('canvas[data-kw-qr]');
    if (qrCanvas) {
      ctx.drawImage(qrCanvas, 40, 820, 240, 240);
    }

    // QR caption
    ctx.fillStyle = '#0a1f24';
    ctx.font = 'bold 20px Figtree, system-ui, sans-serif';
    ctx.fillText('Scan to verify', 310, 880);
    ctx.fillStyle = '#4b6b6f';
    ctx.font = '500 15px Figtree, system-ui, sans-serif';
    ctx.fillText('Scan this code at the school gate', 310, 910);
    ctx.fillText('to confirm this card is genuine.', 310, 932);

    // Footer
    ctx.fillStyle = '#4b6b6f';
    ctx.font = '500 14px Figtree, system-ui, sans-serif';
    ctx.fillText(`Issued: ${fmtDate(card.issued_at)}`, 40, 1080);
    ctx.fillText(`Expires: ${fmtDate(card.expires_at)}`, 40, 1108);
    ctx.font = '700 14px Figtree, system-ui, sans-serif';
    ctx.fillStyle = '#00abc9';
    ctx.fillText('kidswiseapp.com', W - 220, 1108);

    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `kidswiseapp-id-${(card.staff_id_code || 'staff').toLowerCase()}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success('ID card saved to your downloads');
  }

  if (!open) return null;
  const active = card && card.is_active && card.status !== 'revoked';

  return (
    <div data-testid="staff-id-modal" style={overlayStyle} onClick={onClose}>
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        <div style={dialogHeaderStyle}>
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800 }}>
            🪪 {isAdminView ? `Staff ID Card${targetLabel ? ` — ${targetLabel}` : ''}` : 'My Staff ID Card'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="staff-id-modal-close"
            style={closeBtnStyle}
            aria-label="Close"
          >×</button>
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#4b6b6f' }}>Loading…</div>
        ) : !card ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#4b6b6f' }}>
            Could not load your ID card.
          </div>
        ) : (
          <>
            {/* The visual badge */}
            <div ref={badgeRef} data-testid="staff-id-badge" style={badgeShellStyle}>
              <div style={{
                ...badgeHeaderStyle,
                background: card.organization?.primary_color
                  ? `linear-gradient(135deg, ${card.organization.primary_color} 0%, #2abb9b 100%)`
                  : BRAND_GRADIENT,
              }}>
                {card.organization?.logo_url ? (
                  <img
                    src={card.organization.logo_url}
                    alt=""
                    style={{ height: 32, width: 'auto', borderRadius: 6, background: '#fff', padding: 4 }}
                  />
                ) : (
                  <div style={badgeLogoFallback}>
                    {(card.organization?.name || 'KW').slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', letterSpacing: '-0.01em' }}>
                    {card.organization?.name || 'Kids WiseApp'}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.92)', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700 }}>
                    Staff Identification
                  </div>
                </div>
              </div>

              <div style={badgeBodyStyle}>
                {/* Avatar — now the visual hero of the card. Big circle,
                    centered, with the camera-overlay anchored to it.
                    Name + role sit *underneath* (proper ID-badge layout)
                    so the photo stays prominent at arm's-length from
                    the school gate. */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 16 }}>
                  <div style={avatarWrapStyle} data-testid="staff-id-avatar-wrap">
                    {card.avatar_url ? (
                      <img src={card.avatar_url} alt="" style={avatarImgStyle} />
                    ) : (
                      <div style={avatarFallbackStyle}>
                        {(card.full_name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    {/* Camera overlay — hidden in admin view since photo
                        upload uses self-only endpoints (/api/me/avatar). */}
                    {!isAdminView && (
                      <>
                        <button
                          type="button"
                          onClick={() => photoInputRef.current?.click()}
                          data-testid="staff-id-photo-upload-btn"
                          disabled={photoBusy}
                          style={photoOverlayBtnStyle}
                          title={
                            card.avatar_status === 'pending'
                              ? 'Replace pending photo'
                              : (card.avatar_url ? 'Replace photo' : 'Upload a photo for manager approval')
                          }
                          aria-label={
                            card.avatar_status === 'pending'
                              ? 'Replace pending photo'
                              : (card.avatar_url ? 'Replace photo' : 'Upload a photo for manager approval')
                          }
                        >
                          {photoBusy ? '…' : '📷'}
                        </button>
                        <input
                          ref={photoInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
                          onChange={handlePhotoSelected}
                          data-testid="staff-id-photo-input"
                          style={{ display: 'none' }}
                        />
                      </>
                    )}
                  </div>
                  <div style={{ textAlign: 'center', marginTop: 12 }}>
                    <div data-testid="staff-id-name" style={{ fontSize: 22, fontWeight: 800, color: '#0a1f24', lineHeight: 1.1 }}>
                      {card.full_name}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#4b6b6f', marginTop: 4 }}>
                      {card.role_label}
                      {card.assigned_room_name ? ` · ${card.assigned_room_name}` : ''}
                    </div>
                    {card.avatar_url && !isAdminView && (
                      <button
                        type="button"
                        onClick={handlePhotoRemove}
                        disabled={photoBusy}
                        data-testid="staff-id-photo-remove-btn"
                        style={{
                          background: 'transparent', border: 'none', padding: 0,
                          marginTop: 6,
                          fontSize: 11, color: '#9b2c2c', fontWeight: 600,
                          cursor: photoBusy ? 'wait' : 'pointer',
                        }}
                      >
                        Remove photo
                      </button>
                    )}
                  </div>
                </div>

                {/* Photo approval status — only relevant for staff who
                    self-uploaded a photo that's awaiting manager review,
                    or for a previous upload that was rejected. Managers
                    auto-approve, so they will never see this block. */}
                {card.avatar_status === 'pending' && card.avatar_pending_url && (
                  <div data-testid="staff-id-photo-pending-banner" style={photoPendingBannerStyle}>
                    <img
                      src={card.avatar_pending_url}
                      alt="Pending photo preview"
                      style={photoPendingThumbStyle}
                      data-testid="staff-id-photo-pending-thumb"
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#8a5a07', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                        ⏳ Awaiting manager approval
                      </div>
                      <div style={{ fontSize: 12, color: '#5b4419', marginTop: 4, lineHeight: 1.4 }}>
                        Your photo won't appear on your ID card until a manager
                        verifies it. We'll email you as soon as it's reviewed.
                      </div>
                    </div>
                  </div>
                )}
                {card.avatar_status === 'rejected' && (
                  <div data-testid="staff-id-photo-rejected-banner" style={photoRejectedBannerStyle}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#9b2c2c', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      ✕  Last photo was rejected
                    </div>
                    {card.avatar_rejection_reason ? (
                      <div style={{ fontSize: 12, color: '#651818', marginTop: 4, lineHeight: 1.4 }}>
                        <strong>Reason:</strong> {card.avatar_rejection_reason}
                      </div>
                    ) : null}
                    <div style={{ fontSize: 12, color: '#651818', marginTop: 4, lineHeight: 1.4 }}>
                      Please upload a fresh photo and your manager will review it again.
                    </div>
                  </div>
                )}

                <div style={idCodeBoxStyle}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#4b6b6f', letterSpacing: '0.14em' }}>STAFF ID</div>
                  <div data-testid="staff-id-code" style={{
                    fontFamily: 'ui-monospace, Menlo, monospace',
                    fontSize: 22, fontWeight: 800, color: '#0a1f24', marginTop: 2,
                  }}>
                    {card.staff_id_code}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                  <span data-testid="staff-id-status-pill" style={active ? pillActiveStyle : pillRevokedStyle}>
                    {active ? '✓  Authorised staff' : `✕  ${card.status === 'revoked' ? 'Revoked' : 'Expired'}`}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                  <div style={{ background: '#fff', padding: 6, borderRadius: 10, border: '1px solid rgba(10,31,36,0.08)' }}>
                    <QRCodeCanvas
                      value={card.verify_url || ''}
                      size={120}
                      bgColor="#ffffff"
                      fgColor="#0a1f24"
                      level="M"
                      data-kw-qr=""
                      includeMargin={false}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0a1f24' }}>Scan to verify</div>
                    <div style={{ fontSize: 11, color: '#4b6b6f', lineHeight: 1.45, marginTop: 4 }}>
                      The receiving school can scan this QR to confirm
                      that this is a current Kids WiseApp staff member.
                    </div>
                  </div>
                </div>

                <div style={badgeFooterStyle}>
                  <span data-testid="staff-id-issued">Issued: {fmtDate(card.issued_at)}</span>
                  <span data-testid="staff-id-expires">Expires: {fmtDate(card.expires_at)}</span>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div style={actionRowStyle}>
              <button
                type="button"
                onClick={handleDownload}
                data-testid="staff-id-download-btn"
                style={primaryActionBtnStyle}
                disabled={busy}
              >
                ⬇ Download PNG
              </button>
              <button
                type="button"
                onClick={handleCopy}
                data-testid="staff-id-copy-btn"
                style={ghostActionBtnStyle}
                disabled={busy || !card.verify_url}
              >
                🔗 Copy verify link
              </button>
              <button
                type="button"
                onClick={handleRotate}
                data-testid="staff-id-rotate-btn"
                style={ghostActionBtnStyle}
                disabled={busy}
              >
                🔁 Rotate link
              </button>
            </div>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.72rem', color: '#4b6b6f', textAlign: 'center' }}>
              Show this card at the gate, or share the verify link in advance.
              Rotate the link if you suspect anyone else has it.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// ---- Helpers ----

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function drawInitialAvatar(ctx, ax, ay, ar, fullName) {
  ctx.fillStyle = '#cdeff5';
  ctx.beginPath();
  ctx.arc(ax + ar, ay + ar, ar, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#00abc9';
  ctx.font = 'bold 64px Figtree, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(((fullName || '?')[0] || '').toUpperCase(), ax + ar, ay + ar + 4);
  ctx.textAlign = 'start';
  ctx.textBaseline = 'alphabetic';
}

function roundRect(ctx, x, y, w, h, r) {
  const radii = Array.isArray(r) ? r : [r, r, r, r];
  ctx.beginPath();
  ctx.moveTo(x + radii[0], y);
  ctx.lineTo(x + w - radii[1], y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radii[1]);
  ctx.lineTo(x + w, y + h - radii[2]);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radii[2], y + h);
  ctx.lineTo(x + radii[3], y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radii[3]);
  ctx.lineTo(x, y + radii[0]);
  ctx.quadraticCurveTo(x, y, x + radii[0], y);
  ctx.closePath();
}

// ---- Styles ----

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 1000,
  background: 'rgba(6,34,42,0.55)',
  backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '1rem',
};
const dialogStyle = {
  background: '#fff',
  borderRadius: 22,
  width: '100%', maxWidth: 460,
  maxHeight: '92vh', overflowY: 'auto',
  boxShadow: '0 30px 80px rgba(6,34,42,0.30)',
};
const dialogHeaderStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 18px',
  borderBottom: '1px solid rgba(10,31,36,0.08)',
};
const closeBtnStyle = {
  background: 'transparent', border: 'none',
  fontSize: 24, lineHeight: 1, cursor: 'pointer',
  width: 32, height: 32, borderRadius: 8,
  color: '#4b6b6f',
};
const badgeShellStyle = {
  margin: '14px 18px 0',
  borderRadius: 18,
  overflow: 'hidden',
  border: '1px solid rgba(10,31,36,0.08)',
  boxShadow: '0 12px 30px rgba(6,34,42,0.12)',
  background: '#fff',
};
const badgeHeaderStyle = {
  padding: '14px 16px',
  display: 'flex', alignItems: 'center', gap: 12,
};
const badgeLogoFallback = {
  width: 36, height: 36,
  borderRadius: 8,
  background: 'rgba(255,255,255,0.92)',
  color: '#00abc9',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 13, fontWeight: 800,
};
const badgeBodyStyle = {
  padding: '14px 16px 16px',
};
const avatarWrapStyle = {
  position: 'relative',
  width: 130, height: 130,
  flexShrink: 0,
};
const avatarImgStyle = {
  width: 130, height: 130, borderRadius: '50%', objectFit: 'cover',
  border: '3px solid #fff', boxShadow: '0 10px 28px rgba(0,171,201,0.32)',
  display: 'block',
};
const avatarFallbackStyle = {
  width: 130, height: 130, borderRadius: '50%',
  background: '#cdeff5', color: '#00abc9',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 56, fontWeight: 800,
  border: '3px solid #fff', boxShadow: '0 10px 28px rgba(0,171,201,0.32)',
};
const photoOverlayBtnStyle = {
  position: 'absolute', bottom: 0, right: 0,
  width: 38, height: 38, borderRadius: '50%',
  background: BRAND_GRADIENT,
  color: '#fff',
  border: '3px solid #fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 16, cursor: 'pointer',
  boxShadow: '0 6px 14px rgba(0,171,201,0.45)',
  padding: 0,
};
const idCodeBoxStyle = {
  background: '#f4fbfc',
  border: '1px solid rgba(10,31,36,0.06)',
  borderRadius: 12,
  padding: '10px 12px',
  marginBottom: 12,
};
const pillActiveStyle = {
  display: 'inline-flex', alignItems: 'center',
  background: '#dbf5ef', color: '#0b6b58',
  fontSize: 12, fontWeight: 800,
  padding: '6px 12px', borderRadius: 999,
  letterSpacing: '0.04em',
};
const pillRevokedStyle = {
  display: 'inline-flex', alignItems: 'center',
  background: '#fde4e4', color: '#9b2c2c',
  fontSize: 12, fontWeight: 800,
  padding: '6px 12px', borderRadius: 999,
};
const photoPendingBannerStyle = {
  display: 'flex', alignItems: 'center', gap: 12,
  background: '#fff7e0',
  border: '1px solid rgba(180, 130, 20, 0.30)',
  borderRadius: 12,
  padding: '10px 12px',
  marginBottom: 12,
};
const photoPendingThumbStyle = {
  width: 48, height: 48, borderRadius: '50%',
  objectFit: 'cover',
  border: '2px solid #fff',
  boxShadow: '0 2px 6px rgba(0,0,0,0.10)',
  flexShrink: 0,
};
const photoRejectedBannerStyle = {
  background: '#fde4e4',
  border: '1px solid rgba(155, 44, 44, 0.30)',
  borderRadius: 12,
  padding: '10px 12px',
  marginBottom: 12,
};
const badgeFooterStyle = {
  display: 'flex', justifyContent: 'space-between',
  fontSize: 11, color: '#4b6b6f',
  borderTop: '1px dashed rgba(10,31,36,0.10)',
  paddingTop: 10,
};
const actionRowStyle = {
  display: 'flex', gap: 8, flexWrap: 'wrap',
  padding: '14px 18px 6px',
  justifyContent: 'center',
};
const primaryActionBtnStyle = {
  background: BRAND_GRADIENT,
  color: '#fff', border: 'none',
  borderRadius: 12, padding: '10px 16px',
  fontWeight: 700, fontSize: 13,
  boxShadow: '0 8px 18px rgba(0,171,201,0.28)',
  cursor: 'pointer',
};
const ghostActionBtnStyle = {
  background: '#fff',
  color: '#0a1f24',
  border: '1px solid rgba(10,31,36,0.14)',
  borderRadius: 12, padding: '10px 14px',
  fontWeight: 600, fontSize: 13,
  cursor: 'pointer',
};

export default StaffIDCardModal;
